import "@xyflow/react/dist/style.css";
import { useMsal } from "@azure/msal-react";
import { Alert, Center, Loader } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import {
    Background,
    BackgroundVariant,
    Controls,
    type Node,
    type NodeMouseHandler,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect, useRef } from "react";
import { BucketNode } from "./BucketNode";
import { useOverviewGraph } from "./hooks/useOverviewGraph";
import { subFacetsQueryOptions } from "./services/subFacetsQuery";
import { type ExpandGraphProps, type ExplodeNodeData, NodeKind, NodeRole } from "./types";

const HOVER_INTENT_MS = 150;

const nodeTypes = { [NodeKind.BUCKET]: BucketNode };
const defaultEdgeOptions = { type: "default", style: { stroke: "#c0b6e8", strokeWidth: 1.5 } };
const proOptions = { hideAttribution: true };
const fitViewOptions = { padding: 0.1, maxZoom: 1 };
const fullFitOptions = { padding: 0.12, maxZoom: 1, duration: 500 };
const focusFitOptions = { padding: 0.22, maxZoom: 1.75, duration: 500 };

const FitOnChange = ({ trigger }: { trigger: string }) => {
    const { fitView, getNodes, getNode } = useReactFlow();
    useEffect(() => {
        const parts = trigger.split("|");
        const focusing = Boolean(parts[1]) || Boolean(parts[2]);
        const ids = focusing
            ? getNodes()
                  .filter((graphNode) => !(graphNode.data as ExplodeNodeData).dimmed)
                  .map((graphNode) => graphNode.id)
            : [];
        const isMeasured = (id: string) => Boolean(getNode(id)?.measured?.width);
        let frame = 0;
        let tries = 0;
        const attempt = () => {
            tries += 1;
            if (ids.length && !ids.every(isMeasured) && tries < 40) {
                frame = requestAnimationFrame(attempt);
                return;
            }
            fitView(
                ids.length
                    ? { ...focusFitOptions, nodes: ids.map((id) => ({ id })) }
                    : fullFitOptions,
            );
        };
        frame = requestAnimationFrame(attempt);
        return () => cancelAnimationFrame(frame);
    }, [trigger, fitView, getNodes, getNode]);
    return null;
};

const Canvas = ({
    index,
    rootLabel,
    category,
    categoryLabel,
    fields,
    gridPage,
    selected,
    overlay,
    onGridPage,
    onSelect,
    onClearSelection,
    onOpen,
    onOpenOverlay,
    onCloseOverlay,
}: ExpandGraphProps) => {
    const { instance } = useMsal();
    const queryClient = useQueryClient();
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const query = useOverviewGraph({
        index,
        rootLabel,
        category,
        categoryLabel,
        fields,
        gridPage,
        selected,
        overlay,
    });
    const flow = query.data ?? { nodes: [], edges: [] };

    const autoOpened = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!query.noSubGroups || !selected) return;
        const key = `${selected.field}|${selected.value}`;
        if (autoOpened.current === key) return;
        autoOpened.current = key;
        onOpen([selected]);
        onClearSelection();
    }, [query.noSubGroups, selected, onOpen, onClearSelection]);

    const clearHoverTimer = useCallback(() => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = undefined;
        }
    }, []);

    const handleNodeMouseEnter = useCallback<NodeMouseHandler>(
        (_, node) => {
            const { role, selection, skeleton } = (node as Node<ExplodeNodeData>).data;
            if (skeleton || role !== NodeRole.GROUP || selection.length !== 1 || selected) return;
            clearHoverTimer();
            hoverTimer.current = setTimeout(() => {
                hoverTimer.current = undefined;
                queryClient.prefetchQuery(
                    subFacetsQueryOptions({ instance, index, locks: selection }),
                );
            }, HOVER_INTENT_MS);
        },
        [clearHoverTimer, queryClient, instance, index, selected],
    );

    useEffect(() => clearHoverTimer, [clearHoverTimer]);

    const handleClick = useCallback<NodeMouseHandler>(
        (_, node) => {
            const nodeData = (node as Node<ExplodeNodeData>).data;
            const { role, selection, skeleton, overlayPage } = nodeData;
            if (skeleton) return;
            if (nodeData.overlay) {
                onOpenOverlay({ ...nodeData.overlay, page: overlayPage ?? 0 });
                return;
            }
            if (nodeData.gridPage !== undefined) {
                onGridPage(nodeData.gridPage);
                return;
            }
            if (role !== NodeRole.GROUP || selection.length === 0) return;
            if (selection.length === 1) {
                onSelect(selection[0]);
                return;
            }
            onOpen(selection);
        },
        [onSelect, onOpen, onOpenOverlay, onGridPage],
    );

    const handlePaneClick = useCallback(() => {
        if (overlay) {
            onCloseOverlay();
            return;
        }
        if (selected) onClearSelection();
    }, [overlay, onCloseOverlay, selected, onClearSelection]);

    if (query.isError) return <Alert color="red">Failed to load facets for this view.</Alert>;
    if (!category) return <Center h={240}>Pick a category to visualize.</Center>;
    if (query.isLoading)
        return (
            <Center h={240}>
                <Loader color="purple" />
            </Center>
        );

    return (
        <div
            style={{
                height: "calc(100vh - 250px)",
                width: "100%",
                border: "1px solid #dee2e6",
                borderRadius: 8,
            }}
        >
            <ReactFlow
                nodes={flow.nodes}
                edges={flow.edges}
                nodeTypes={nodeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                proOptions={proOptions}
                onNodeClick={handleClick}
                onNodeMouseEnter={handleNodeMouseEnter}
                onNodeMouseLeave={clearHoverTimer}
                onPaneClick={handlePaneClick}
                nodesDraggable={false}
                fitView
                fitViewOptions={fitViewOptions}
                minZoom={0.2}
                maxZoom={2}
            >
                <FitOnChange
                    trigger={[
                        category,
                        selected ? `${selected.field}:${selected.value}` : "",
                        overlay ? overlay.field : "",
                        query.subLoading ? "L" : "",
                    ].join("|")}
                />
                <Background variant={BackgroundVariant.Dots} gap={24} color="#e9ecef" />
                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );
};

export const ExpandGraph = (props: ExpandGraphProps) => (
    <ReactFlowProvider>
        <Canvas {...props} />
    </ReactFlowProvider>
);
